/* =============================================================================
   Chart theme — the shared runtime every chart in this folder composes
   -----------------------------------------------------------------------------
   Every colour this family paints is READ from the CSS custom properties on
   <html> at render time. There is no hex value, no rgb() and no Tailwind
   palette colour anywhere under `src/components/charts/`, so re-theming is a
   token change and never a code change.

   Why reading at render time re-themes without a reload: ThemeProvider writes
   the resolved theme onto `document.documentElement` synchronously inside its
   own setState path — setChoice, cycle and the storage listener all call
   applyTheme() BEFORE setResolved() (src/theme/ThemeProvider.tsx:42-56 and
   73-83). React therefore re-renders only once the attribute is already on the
   DOM, so a memo keyed on `resolved` reads the new computed values.
   ============================================================================= */

import { useMemo } from 'react';
import { useReducedMotion } from 'motion/react';

import { formatDate } from '../../i18n/format';
import { useTheme } from '../../theme/ThemeProvider';

export interface ChartTheme {
  /** `--chart-1` … `--chart-6`, in order. Colourblind-checked by the token set. */
  series: readonly string[];
  /** `--chart-grid` — axis split lines. */
  grid: string;
  /** `--text-muted` — axis labels and ticks. */
  textMuted: string;
  /** `--text-heading` — values and tooltip text. */
  textHeading: string;
  /** `--surface` — tooltip and slice-label background. */
  surface: string;
  /** `--border` — axis lines and the flow chart's connectors. */
  border: string;
  /** `--font-body`, as a canvas font-family list. */
  fontFamily: string;
  /** `--fs-2xs` / `--fs-xs`, resolved to canvas pixels at the root font size. */
  fontSize2xs: number;
  fontSizeXs: number;
  /** `--dur-reveal` — the entrance animation length, in ms. */
  durationReveal: number;
  /** `--stagger-step` — the per-item entrance delay, in ms. */
  staggerStep: number;
}

export interface ChartMotion {
  /** True when charts should play their entrance animation. */
  active: boolean;
  duration: number;
  easing: string;
  /** Staggered delay for item `index`, in ms. */
  delay: (index: number) => number;
}

/**
 * The value used for a colour token when there is no DOM to read from. That
 * cannot happen here — src/main.tsx mounts from `document.getElementById` at
 * module scope and there is no SSR path — and it is deliberately not a colour
 * literal, because this folder never invents a palette value.
 */
const NO_DOCUMENT_COLOR = 'currentColor';
const NO_DOCUMENT_FONT = 'sans-serif';

function parseFontSize(value: string, rootFontSize: number): number {
  const size = Number.parseFloat(value);
  if (!Number.isFinite(size)) return 0;
  return value.trim().endsWith('rem') ? size * rootFontSize : size;
}

function parseMilliseconds(value: string): number {
  const ms = Number.parseFloat(value);
  return Number.isFinite(ms) ? ms : 0;
}

function buildTheme(read: (name: string) => string, rootFontSize: number): ChartTheme {
  const color = (name: string) => read(name).trim() || NO_DOCUMENT_COLOR;

  return {
    series: [
      color('--chart-1'),
      color('--chart-2'),
      color('--chart-3'),
      color('--chart-4'),
      color('--chart-5'),
      color('--chart-6'),
    ],
    grid: color('--chart-grid'),
    textMuted: color('--text-muted'),
    textHeading: color('--text-heading'),
    surface: color('--surface'),
    border: color('--border'),
    fontFamily: read('--font-body').trim() || NO_DOCUMENT_FONT,
    fontSize2xs: parseFontSize(read('--fs-2xs'), rootFontSize),
    fontSizeXs: parseFontSize(read('--fs-xs'), rootFontSize),
    durationReveal: parseMilliseconds(read('--dur-reveal')),
    staggerStep: parseMilliseconds(read('--stagger-step')),
  };
}

/** Reads the live theme off the document root. Safe to call anywhere. */
export function readChartTheme(): ChartTheme {
  if (typeof document === 'undefined') return buildTheme(() => '', 16);

  const style = getComputedStyle(document.documentElement);
  return buildTheme(
    (name) => style.getPropertyValue(name),
    Number.parseFloat(style.fontSize) || 16,
  );
}

/**
 * The theme object every chart in this folder uses. Memoised because the read
 * itself is eleven synchronous `getComputedStyle` lookups plus a parse — a
 * real cost per render, not a speculative one — and `resolved` is the only
 * input that can change those custom properties.
 */
export function useChartTheme(): ChartTheme {
  const { resolved } = useTheme();
  return useMemo(() => readChartTheme(), [resolved]);
}

/** The series colour for slot `index`, cycling through the six token slots. */
export function seriesColor(theme: ChartTheme, index: number): string {
  return theme.series[index % theme.series.length];
}

/**
 * Motion policy for the chart family, under the project's Heavy animation
 * level: entrance and axis animation on first render, and the final state
 * rendered immediately when the user prefers reduced motion.
 */
export function useChartMotion(theme: ChartTheme): ChartMotion {
  const reduced = useReducedMotion();

  return useMemo(() => {
    // `null` means "not resolved yet" — only an explicit `true` disables motion.
    const active = reduced !== true;
    return {
      active,
      duration: active ? theme.durationReveal : 0,
      // ECharts has no cubic-bezier easing, so this is the nearest built-in to
      // the --ease-out curve the rest of the system animates on.
      easing: 'cubicOut',
      delay: (index: number) => (active ? index * theme.staggerStep : 0),
    };
  }, [reduced, theme.durationReveal, theme.staggerStep]);
}

/** The ECharts animation keys every chart in this family spreads in. */
export function entranceAnimation(motion: ChartMotion) {
  return {
    animation: motion.active,
    animationDuration: motion.duration,
    animationDurationUpdate: motion.duration,
    animationEasing: motion.easing,
  };
}

/** Axis line, tick, label and split-line colours, straight from the tokens. */
export function axisTokens(theme: ChartTheme) {
  return {
    axisLine: { lineStyle: { color: theme.border } },
    axisTick: { lineStyle: { color: theme.border } },
    splitLine: { lineStyle: { color: theme.grid } },
    axisLabel: {
      color: theme.textMuted,
      fontFamily: theme.fontFamily,
      fontSize: theme.fontSizeXs,
    },
  };
}

/**
 * Tooltip chrome. ECharts ships a fixed light tooltip by default, which would
 * stay light in dark mode, so every colour here is a token.
 *
 * `valueFormatter` is used rather than `formatter` on purpose: it lets ECharts
 * render its own template and only hands us the numeric value, so no chart in
 * this folder builds HTML that ECharts injects with innerHTML.
 */
export function tooltipTokens(theme: ChartTheme, valueFormatter?: (value: number) => string) {
  return {
    backgroundColor: theme.surface,
    borderColor: theme.border,
    borderWidth: 1,
    padding: [6, 10],
    textStyle: {
      color: theme.textHeading,
      fontFamily: theme.fontFamily,
      fontSize: theme.fontSizeXs,
    },
    // The chart tooltip is an overlay, so it takes the one overlay elevation
    // language: the hard offset. A soft blur was the other answer, and it is the
    // one that reads as a smudge against the dark canvas.
    extraCssText: 'box-shadow: var(--shadow-hard); border-radius: var(--radius-xs);',
    axisPointer: {
      lineStyle: { color: theme.border },
      crossStyle: { color: theme.border },
      label: { backgroundColor: theme.textHeading, color: theme.surface },
    },
    // Passed through by reference rather than wrapped: every caller hands in a
    // module-scope formatter, and echarts-for-react deep-compares the option
    // with fast-deep-equal (lib/core.js:52), which compares functions by
    // identity. A fresh arrow here would make every re-render look like a
    // change and force a needless setOption.
    ...(valueFormatter ? { valueFormatter } : {}),
  };
}

/**
 * A fixture month (`YYYY-MM`) as a LOCAL-time `Date`.
 *
 * `new Date('2026-01')` is parsed as UTC midnight, because ECMA-262 treats a
 * date-ONLY form as UTC; in any negative-offset zone that renders as the
 * previous December (measured: TZ=America/Chicago gives "Wed Dec 31 2025" for
 * `new Date('2026-01-01')`). The `T00:00:00` suffix makes it a date-TIME form,
 * which the same spec parses as local time, so the month never shifts.
 */
export function monthDate(month: string): Date {
  return new Date(`${month}-01T00:00:00`);
}

/** `2026-01` → the locale's own month-year text, through the house formatter. */
export function formatMonth(month: string): string {
  return formatDate(monthDate(month), { style: 'monthYear' });
}
